//
// PreloadedSvg
//
// A component for SVGs that are used many times (e.g., hundreds in a Table).
// SVGs are fetched and inlined at module load time, shared across all instances.
//
// This approach:
// - Works reliably in Safari (which has issues with external <use href>)
// - Preloads SVGs when the module is imported (not on each render)
// - Shares parsed SVG content across all component instances
// - Uses browser HTTP caching for the fetch requests
// - Shows a loading spinner while SVGs are being fetched
//
// Usage:
//   import { PreloadedSvg, preloadSvgs } from './PreloadedSvg';
//
//   // At module level - register SVGs to preload
//   preloadSvgs([
//     '/images/svg/gui/myIcon.svg',
//   ]);
//
//   // In component - with explicit color prop:
//   <PreloadedSvg src="/images/svg/gui/myIcon.svg" color="#ff0000" className="w-5 h-5" />
//
//   // Or with Tailwind fill classes (like SvgColorImg with imgColorAsCssFill):
//   <PreloadedSvg src="/images/svg/gui/myIcon.svg" className="w-5 h-5 fill-pxUiAlert" />
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useSyncExternalStore } from 'react';

import CircularProgress from './CircularProgress';

// Cache: path -> { status: 'loading' | 'loaded' | 'error', content: { viewBox, innerHTML } | null }
const cache = new Map();

// Subscribers for React updates
const listeners = new Set();

// Version counter to trigger React re-renders
let version = 0;

const notifyListeners = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

const subscribe = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

const getSnapshot = () => version;

// Parse SVG string and extract viewBox + inner content
const parseSvgContent = (svgText) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'image/svg+xml');
  const svgElement = doc.querySelector('svg');

  if (!svgElement) {
    return null;
  }

  const viewBox = svgElement.getAttribute('viewBox');
  const innerHTML = svgElement.innerHTML;

  return { viewBox, innerHTML };
};

/**
 * Register SVGs to be preloaded.
 * Call this at module level (outside component) for SVGs you want to preload.
 *
 * @param {string[]} paths - Array of SVG paths to preload
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper belongs with the cache it feeds
export const preloadSvgs = (paths) => {
  paths.forEach((path) => {
    if (cache.has(path)) {
      // Already loading or loaded
      return;
    }

    // Mark as loading
    cache.set(path, { status: 'loading', content: null });

    // Fetch the SVG
    fetch(path)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch SVG: ${response.status}`);
        }
        return response.text();
      })
      .then((svgText) => {
        const parsed = parseSvgContent(svgText);
        if (!parsed) {
          throw new Error('Invalid SVG content');
        }
        cache.set(path, { status: 'loaded', content: parsed });
        notifyListeners();
      })
      .catch(() => {
        cache.set(path, { status: 'error', content: null });
        notifyListeners();
      });
  });
};

/**
 * Component that renders a preloaded SVG with optional color.
 * Shows a loading spinner while the SVG is being fetched.
 *
 * Structure matches SvgColorImg: wrapper div with className, inner svg with fixed class.
 * This allows Tailwind fill-* classes on className to cascade to the SVG paths.
 */
export const PreloadedSvg = function PreloadedSvg(props) {
  const {
    src,
    color,
    className,
    style,
    description = '',
    absolute = false,
  } = props;

  // Subscribe to cache updates
  useSyncExternalStore(subscribe, getSnapshot);

  const entry = cache.get(src);

  // Match SvgColorImg structure: className goes to wrapper div
  const divClassName = classNames(
    'overflow-hidden',
    {
      relative: !absolute,
      absolute,
    },
    className,
  );

  // Only set inline fill/color when color prop is explicitly provided
  // Otherwise, let CSS classes (like fill-*) cascade through
  const svgStyle = color
    ? { fill: color, color }
    : undefined;

  // Not registered for preloading - trigger a fetch now
  if (!entry) {
    preloadSvgs([src]);
    return (
      <div className={divClassName} style={style}>
        <CircularProgress full />
      </div>
    );
  }

  // Still loading
  if (entry.status === 'loading') {
    return (
      <div className={divClassName} style={style}>
        <CircularProgress full />
      </div>
    );
  }

  // Error loading
  if ((entry.status === 'error') || (!entry.content)) {
    // Return empty div on error (or could show a fallback icon)
    return <div className={divClassName} style={style} />;
  }

  // Loaded successfully - render inlined SVG inside wrapper div
  // Structure matches SvgColorImg: div wrapper with className, svg with fixed sizing
  return (
    <div
      className={divClassName}
      style={style}
    >
      <svg
        className="w-full h-full overflow-hidden"
        style={svgStyle}
        viewBox={entry.content.viewBox}
        aria-label={description}
        role="img"
        dangerouslySetInnerHTML={{ __html: entry.content.innerHTML }}
      />
    </div>
  );
};

PreloadedSvg.propTypes = {
  src: PropTypes.string.isRequired,
  color: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  description: PropTypes.string,
  absolute: PropTypes.bool,
};

export default PreloadedSvg;
